import { useCallback, useEffect, useRef, useState } from 'react';

const API = `${window.location.protocol}//${window.location.hostname}/api`;

function api(path, opts = {}) {
  const token = localStorage.getItem('token');
  return fetch(`${API}${path}`, {
    ...opts,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...opts.headers },
  }).then(async r => {
    const data = await r.json().catch(() => null);
    if (!r.ok) throw new Error(data?.error || 'Request failed');
    return data;
  });
}

const PRIORITY_COLORS = { critical: '#ef4444', high: '#f97316', medium: '#f59e0b', low: '#3b82f6' };
const CAREER_LABELS   = {
  app_3: 'App III', app_2: 'App II', app_1: 'App I',
  tech_c2: 'Tech C2', tech_c1: 'Tech C1', tech_b: 'Tech B',
  tech_a: 'Tech A', tech_sr: 'Senior Tech',
};

function StatusBadge({ status }) {
  const MAP = {
    pending:   { color: '#f59e0b', label: 'Pending' },
    accepted:  { color: '#22c55e', label: 'Accepted ✓' },
    rejected:  { color: '#ef4444', label: 'Rejected' },
    cancelled: { color: '#64748b', label: 'Cancelled' },
  };
  const { color, label } = MAP[status] || { color: '#64748b', label: status };
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, textTransform: 'uppercase',
      padding: '2px 8px', borderRadius: 4,
      backgroundColor: `${color}22`, color, border: `1px solid ${color}44`,
    }}>{label}</span>
  );
}

function TechRow({ tech, selected, onToggle }) {
  const existingStatus = tech.existing_callout_status;
  const blocked = existingStatus === 'accepted' || existingStatus === 'pending';

  return (
    <div
      onClick={() => !blocked && onToggle(tech.id)}
      style={{
        display: 'flex', alignItems: 'center', gap: 12, padding: '10px 14px',
        borderRadius: 8, marginBottom: 6, cursor: blocked ? 'default' : 'pointer',
        backgroundColor: selected ? '#1e3a5f' : '#1e293b',
        border: `1px solid ${selected ? '#3b82f6' : '#334155'}`,
        opacity: blocked ? 0.6 : 1,
        transition: 'all 0.1s',
      }}
    >
      <div style={{
        width: 20, height: 20, borderRadius: 4, flexShrink: 0,
        backgroundColor: selected ? '#3b82f6' : '#0f172a',
        border: `2px solid ${selected ? '#3b82f6' : '#475569'}`,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
      }}>
        {selected && <span style={{ color: '#fff', fontSize: 13, fontWeight: 900 }}>✓</span>}
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <span style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 14 }}>{tech.name}</span>
          {tech.career_level && (
            <span style={{ fontSize: 10, color: '#94a3b8', backgroundColor: '#0f172a', padding: '1px 6px', borderRadius: 4 }}>
              {CAREER_LABELS[tech.career_level] || tech.career_level}
            </span>
          )}
          {existingStatus && <StatusBadge status={existingStatus} />}
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 2, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          {tech.distance_miles != null && <span>📍 {tech.distance_miles} mi</span>}
          <span>📞 {tech.callout_count_ytd} callouts YTD</span>
          {tech.last_callout_at && (
            <span>Last: {new Date(tech.last_callout_at).toLocaleDateString()}</span>
          )}
          <span>{tech.active_jobs} active job{tech.active_jobs !== 1 ? 's' : ''}</span>
          {tech.skill_match_count > 0 && (
            <span style={{ color: '#22c55e' }}>✓ {tech.skill_match_count} skill match</span>
          )}
        </div>
      </div>
    </div>
  );
}

function LiveStatusPanel({ workItemId, onClose }) {
  const [callouts, setCallouts] = useState([]);
  const intervalRef = useRef(null);

  const refresh = useCallback(() => {
    api(`/callouts/work-item/${workItemId}`).then(setCallouts).catch(() => {});
  }, [workItemId]);

  useEffect(() => {
    refresh();
    intervalRef.current = setInterval(refresh, 3000);
    return () => clearInterval(intervalRef.current);
  }, [refresh]);

  // Also listen for WS callout_updated events
  useEffect(() => {
    const handler = (e) => {
      try {
        const msg = JSON.parse(e.data);
        if (msg.type === 'callout_updated') refresh();
      } catch {}
    };
    window.__fcWs?.addEventListener('message', handler);
    return () => window.__fcWs?.removeEventListener('message', handler);
  }, [refresh]);

  const pending  = callouts.filter(c => c.status === 'pending');
  const accepted = callouts.filter(c => c.status === 'accepted');
  const rejected = callouts.filter(c => c.status === 'rejected');

  return (
    <div>
      <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 12 }}>
        Live response tracking — refreshes every 3 seconds
      </div>

      {accepted.length > 0 && (
        <div style={{ backgroundColor: '#14532d22', border: '1px solid #22c55e44', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ color: '#22c55e', fontWeight: 700, fontSize: 12, marginBottom: 8 }}>ACCEPTED</div>
          {accepted.map(c => (
            <div key={c.id} style={{ color: '#e2e8f0', fontSize: 13, marginBottom: 4 }}>
              {c.tech_name} <span style={{ color: '#64748b', fontSize: 11 }}>
                — {new Date(c.responded_at).toLocaleTimeString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {pending.length > 0 && (
        <div style={{ backgroundColor: '#1e293b', border: '1px solid #f59e0b44', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ color: '#f59e0b', fontWeight: 700, fontSize: 12, marginBottom: 8 }}>AWAITING RESPONSE</div>
          {pending.map(c => (
            <div key={c.id} style={{ color: '#e2e8f0', fontSize: 13, marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
              <span>{c.tech_name}</span>
              <button
                onClick={() => api(`/callouts/${c.id}/cancel`, { method: 'PATCH' }).then(refresh)}
                style={{ fontSize: 10, color: '#64748b', background: 'none', border: 'none', cursor: 'pointer' }}
              >cancel</button>
            </div>
          ))}
        </div>
      )}

      {rejected.length > 0 && (
        <div style={{ backgroundColor: '#1e293b', border: '1px solid #ef444444', borderRadius: 8, padding: 12, marginBottom: 10 }}>
          <div style={{ color: '#ef4444', fontWeight: 700, fontSize: 12, marginBottom: 8 }}>REJECTED</div>
          {rejected.map(c => (
            <div key={c.id} style={{ color: '#94a3b8', fontSize: 13, marginBottom: 4 }}>
              {c.tech_name}
              {c.response_reason && <span style={{ color: '#64748b', fontSize: 11 }}> — "{c.response_reason}"</span>}
            </div>
          ))}
        </div>
      )}

      {callouts.length === 0 && (
        <div style={{ color: '#475569', fontSize: 13, textAlign: 'center', padding: 20 }}>
          No callouts sent yet.
        </div>
      )}

      <button
        onClick={onClose}
        style={{ marginTop: 16, width: '100%', padding: '10px', borderRadius: 8, background: '#1e293b', border: '1px solid #334155', color: '#94a3b8', cursor: 'pointer', fontSize: 13 }}
      >
        Close
      </button>
    </div>
  );
}

export default function CalloutModal({ workItemId, workOrderNumber, priority, title, onClose }) {
  const [candidates, setCandidates] = useState([]);
  const [selected, setSelected]     = useState(new Set());
  const [note, setNote]             = useState('');
  const [loading, setLoading]       = useState(true);
  const [sending, setSending]       = useState(false);
  const [error, setError]           = useState(null);
  const [phase, setPhase]           = useState('select'); // 'select' | 'live'

  useEffect(() => {
    api(`/callouts/candidates/${workItemId}`)
      .then(d => { setCandidates(d.candidates || []); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [workItemId]);

  const toggleTech = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const sendCallout = async () => {
    if (!selected.size) return;
    setSending(true);
    setError(null);
    try {
      await api('/callouts/initiate', {
        method: 'POST',
        body: JSON.stringify({ work_item_id: workItemId, tech_ids: [...selected], note: note || undefined }),
      });
      setPhase('live');
    } catch (e) {
      setError(e.message);
    } finally {
      setSending(false);
    }
  };

  const priorityColor = PRIORITY_COLORS[priority] || '#64748b';

  return (
    <div style={{
      position: 'fixed', inset: 0, backgroundColor: '#00000088', zIndex: 1000,
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20,
    }}>
      <div style={{
        backgroundColor: '#0f172a', border: '1px solid #334155', borderRadius: 12,
        width: '100%', maxWidth: 560, maxHeight: '90vh', display: 'flex', flexDirection: 'column',
        boxShadow: '0 20px 60px #00000099',
      }}>
        {/* Header */}
        <div style={{ padding: '16px 20px', borderBottom: '1px solid #1e293b', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <span style={{
                  fontSize: 11, fontWeight: 800, textTransform: 'uppercase', padding: '2px 8px',
                  borderRadius: 4, backgroundColor: `${priorityColor}22`, color: priorityColor, border: `1px solid ${priorityColor}44`,
                }}>
                  {priority} Callout
                </span>
                <span style={{ color: '#64748b', fontSize: 13 }}>{workOrderNumber || `WO-${workItemId}`}</span>
              </div>
              <div style={{ color: '#e2e8f0', fontWeight: 600, fontSize: 15, marginTop: 4 }}>{title}</div>
            </div>
            <button onClick={onClose} style={{ background: 'none', border: 'none', color: '#64748b', fontSize: 20, cursor: 'pointer' }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {phase === 'live' ? (
            <LiveStatusPanel workItemId={workItemId} onClose={onClose} />
          ) : loading ? (
            <div style={{ color: '#64748b', textAlign: 'center', padding: 40 }}>Loading candidates…</div>
          ) : error ? (
            <div style={{ color: '#ef4444', fontSize: 13, textAlign: 'center', padding: 20 }}>{error}</div>
          ) : (
            <>
              <div style={{ color: '#94a3b8', fontSize: 12, marginBottom: 12 }}>
                Ranked by fewest YTD callouts · distance tiebreak · select one or more
              </div>

              {candidates.length === 0 ? (
                <div style={{ color: '#475569', textAlign: 'center', padding: 30 }}>No qualified techs found.</div>
              ) : (
                candidates.map(t => (
                  <TechRow
                    key={t.id}
                    tech={t}
                    selected={selected.has(t.id)}
                    onToggle={toggleTech}
                  />
                ))
              )}

              <div style={{ marginTop: 16 }}>
                <label style={{ color: '#94a3b8', fontSize: 12, display: 'block', marginBottom: 6 }}>
                  Note to tech (optional)
                </label>
                <textarea
                  value={note}
                  onChange={e => setNote(e.target.value)}
                  placeholder="e.g. Bring fiber splicer, meet at north gate"
                  rows={2}
                  style={{
                    width: '100%', boxSizing: 'border-box', backgroundColor: '#1e293b',
                    border: '1px solid #334155', borderRadius: 8, color: '#e2e8f0',
                    fontSize: 13, padding: '8px 12px', resize: 'vertical',
                  }}
                />
              </div>

              {error && <div style={{ color: '#ef4444', fontSize: 13, marginTop: 10 }}>{error}</div>}

              <button
                onClick={sendCallout}
                disabled={!selected.size || sending}
                style={{
                  marginTop: 16, width: '100%', padding: '12px', borderRadius: 8,
                  backgroundColor: selected.size ? '#ef4444' : '#334155',
                  color: '#fff', fontWeight: 700, fontSize: 14,
                  border: 'none', cursor: selected.size ? 'pointer' : 'default',
                  transition: 'background 0.15s',
                }}
              >
                {sending ? 'Sending…' : `Send Callout to ${selected.size} Tech${selected.size !== 1 ? 's' : ''}`}
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
